import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ArticleImage from '../components/ArticleImage';
import axios from 'axios';
import { Search } from 'lucide-react';
import { articles as mockArticles } from '../mock';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { slug: 'contoh-aplikasi', title: 'Contoh Aplikasi' },
  { slug: 'panduan-edukasi', title: 'Panduan & Edukasi' },
];

const GROUPS = [
  { slug: 'solar-panel', title: 'Sistem Solar Panel' },
  { slug: 'solar-light', title: 'Sistem Solar Lampu Penerangan Jalan' },
  { slug: 'telecom', title: 'Sistem Solar untuk Telecom & Surveillance' },
  { slug: 'ess', title: 'Energy Storage System (ESS) & Baterai' },
  { slug: 'ups', title: 'Sistem UPS' },
];

const tabOf = (a) => a.tab || 'panduan-edukasi';
const groupOf = (a) => a.group || 'solar-panel';

// Nomor & urutan kartu sesuai LAY OUT PENYUSUNAN (PDF): kiri→kanan, atas→bawah
const ARTICLE_NUMBERS = {
  'contoh-sistem-solar-hybrid-powerx-eah-6-6k': 1,
  'contoh-aplikasi-solar-tanpa-baterai-1300-watt': 2,
  'sistem-solar-panel-hybrid-6-6-kw-tanpa-baterai-untuk-beban-rumah-tangga-1-300-watt': 3,
  'contoh-aplikasi-solar-hybrid-baterai-standing-1300-watt': 4,
  'contoh-aplikasi-sistem-kantor-10000-watt-zero-export': 5,
  'contoh-aplikasi-sistem-kantor-15000-watt-zero-export': 6,
  'contoh-aplikasi-sistem-solar-1000-watt-baterai-lead-acid': 7,
  'contoh-aplikasi-sistem-solar-1000-watt-baterai-agm': 8,
};

const fmt = (iso) =>
  new Date(iso).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

function ArticleCard({ a, num }) {
  return (
    <Link
      to={`/articles/${a.slug}`}
      data-testid={`article-card-${a.slug}`}
      className="group block bg-white rounded-2xl overflow-hidden border border-neutral-200 hover:shadow-xl hover:-translate-y-1 transition-all"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-neutral-100">
        <ArticleImage
          src={a.image}
          alt={a.title}
          className="w-full h-full"
          imgClassName="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        {num != null && (
          <span
            data-testid={`article-num-${a.slug}`}
            className="absolute top-3 left-3 z-10 w-10 h-10 rounded-full bg-px-red text-white font-display text-lg font-bold flex items-center justify-center ring-2 ring-white shadow-lg"
          >
            {num}
          </span>
        )}
      </div>
      <div className="p-6">
        <span className="text-[10px] tracking-[0.25em] text-px-red font-bold">
          {a.tag}
        </span>
        <h3 className="font-display text-xl font-bold text-neutral-900 leading-snug mt-2 mb-3 group-hover:text-px-red transition-colors line-clamp-2">
          {a.title}
        </h3>
        <p className="text-sm text-neutral-600 leading-relaxed line-clamp-3">
          {a.excerpt}
        </p>
      </div>
    </Link>
  );
}

export default function Articles() {
  const [articles, setArticles] = useState(mockArticles);
  const [tab, setTab] = useState('contoh-aplikasi');
  const [q, setQ] = useState('');

  useEffect(() => {
    axios
      .get(`${API}/api/site/articles`)
      .then((r) => {
        if (r.data?.length) setArticles(r.data);
      })
      .catch(() => {});
  }, []);

  const searching = q.trim().length > 0;

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return [];
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(query) ||
        a.excerpt.toLowerCase().includes(query) ||
        a.tag.toLowerCase().includes(query)
    );
  }, [articles, q]);

  const tabArticles = useMemo(() => {
    const list = articles.filter((a) => tabOf(a) === tab);
    return [...list].sort(
      (a, b) => (ARTICLE_NUMBERS[a.slug] ?? 999) - (ARTICLE_NUMBERS[b.slug] ?? 999)
    );
  }, [articles, tab]);

  const activeTab = TABS.find((t) => t.slug === tab);

  return (
    <div data-testid="page-articles" className="pt-[68px] md:pt-[100px] bg-white">
      {/* Hero */}
      <section className="relative bg-neutral-950 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-neutral-950 to-black" />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(239,68,68,0.35), transparent 50%), radial-gradient(circle at 80% 60%, rgba(239,68,68,0.2), transparent 50%)',
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-20 md:py-28 text-white">
          <p className="text-[11px] tracking-[0.3em] text-red-400 font-semibold mb-4">
            NEWS & ARTIKEL
          </p>
          <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] max-w-3xl mb-5">
            Wawasan dari tim PowerX
          </h1>
          <p className="text-neutral-300 max-w-2xl text-base md:text-lg">
            Panduan sizing, perbandingan teknologi, dan studi kasus nyata —
            diracik oleh tim lapangan kami agar keputusan pembelian Anda tepat
            sejak awal.
          </p>
        </div>
      </section>

      {/* Tab bar */}
      <section className="sticky top-[68px] md:top-[100px] z-30 bg-white/95 backdrop-blur border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center bg-neutral-100 rounded-full px-4 py-2 w-full md:max-w-xs focus-within:ring-2 focus-within:ring-red-200">
            <Search className="w-4 h-4 text-neutral-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari artikel..."
              data-testid="articles-search"
              className="bg-transparent outline-none text-sm ml-2 w-full"
            />
          </div>
          <div className="flex gap-2">
            {TABS.map((t) => {
              const active = tab === t.slug && !searching;
              return (
                <button
                  key={t.slug}
                  onClick={() => setTab(t.slug)}
                  data-testid={`articles-tab-${t.slug}`}
                  className={`relative px-6 py-2.5 rounded-full text-sm font-semibold border transition-colors ${
                    active
                      ? 'bg-neutral-900 text-white border-neutral-900'
                      : 'bg-white text-px-red border-red-300 hover:border-px-red animate-pulse'
                  }`}
                >
                  {!active && (
                    <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-15 pointer-events-none" />
                  )}
                  <span className="relative">{t.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14">
        {searching ? (
          <>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-neutral-900 mb-8">
              Hasil pencarian ({results.length})
            </h2>
            {results.length === 0 ? (
              <p className="text-center text-neutral-500 py-20" data-testid="articles-empty">
                Tidak ada artikel yang cocok dengan pencarian Anda.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.map((a) => (
                  <ArticleCard key={a.slug} a={a} />
                ))}
              </div>
            )}
          </>
        ) : (
          (() => {
            const sections = GROUPS.map((g) => ({
              ...g,
              items: tabArticles.filter((a) => groupOf(a) === g.slug),
            })).filter((g) => g.items.length > 0);

            // Nomor: sesuai ARTICLE_NUMBERS (PDF); fallback by-posisi untuk yg belum terdaftar
            const numMap = new Map(
              tabArticles.map((a, i) => [
                a.slug,
                ARTICLE_NUMBERS[a.slug] ?? tabArticles.length - i,
              ])
            );

            if (sections.length === 0) {
              return (
                <p className="text-center text-neutral-500 py-20" data-testid="articles-empty">
                  Belum ada artikel pada kategori ini.
                </p>
              );
            }

            return sections.map((g) => (
              <div
                key={g.slug}
                data-testid={`articles-group-${tab}-${g.slug}`}
                className="mb-16 last:mb-0"
              >
                <div className="flex items-center gap-4 mb-8">
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-neutral-900 uppercase tracking-tight">
                    {activeTab.title} {g.title}
                  </h2>
                  <div className="flex-1 h-px bg-neutral-200" />
                  <span className="text-xs font-semibold text-neutral-400 whitespace-nowrap">
                    {g.items.length} artikel
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {g.items.map((a) => (
                    <ArticleCard key={a.slug} a={a} num={numMap.get(a.slug)} />
                  ))}
                </div>
              </div>
            ));
          })()
        )}
      </section>
    </div>
  );
}
